'use client';

import Icon from '@/components/ui/AppIcon';

interface Lead {
  name: string;
  email: string;
  phone: string;
  whatsapp: string;
  message: string;
  bookingDetails: {
    type: 'flight' | 'hotel' | 'holiday';
    from?: string;
    to?: string;
    destination?: string;
    date?: string;
    passengers?: number;
    price?: number;
  };
  timestamp: string;
  status: 'new' | 'contacted' | 'quoted' | 'booked' | 'cancelled';
  bookingType: 'lead' | 'direct';
  paymentStatus?: 'pending' | 'paid' | 'refunded';
}

interface LeadCardProps {
  lead: {
    id: string;
    name: string;
    email: string;
    phone: string;
    whatsapp: string;
    message: string;
    bookingDetails: {
      type: 'flight' | 'hotel' | 'holiday';
      from?: string;
      to?: string;
      destination?: string;
      date?: string;
      passengers?: number;
      price?: number;
    };
    timestamp: string;
    status: 'new' | 'contacted' | 'quoted' | 'booked' | 'cancelled';
    bookingType: 'lead' | 'direct';
    paymentStatus?: 'pending' | 'paid' | 'refunded';
  };
  onStatusChange: (status: any) => void;
  onDelete: () => void;
}

const LeadCard = ({ lead, onStatusChange, onDelete }: LeadCardProps) => {
  const statusColors = {
    new: 'bg-blue-100 text-blue-800 border-blue-200',
    contacted: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    quoted: 'bg-purple-100 text-purple-800 border-purple-200',
    booked: 'bg-green-100 text-green-800 border-green-200',
    cancelled: 'bg-red-100 text-red-800 border-red-200',
  };

  const handleWhatsApp = () => {
    const message = `Hi ${lead.name}! Thank you for your booking inquiry. I'm ready to help you with your ${lead.bookingDetails.type} booking.`;
    window.open(`https://wa.me/${lead.whatsapp.replace(/\D/g, '')}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleEmail = () => {
    const subject = `Your ${lead.bookingDetails.type} Booking Inquiry`;
    const body = `Hi ${lead.name},\n\nThank you for your interest in booking with DIAL FOR FLIGHT.\n\nBest regards,\nDIAL FOR FLIGHT Team`;
    window.location.href = `mailto:${lead.email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCall = () => {
    window.location.href = `tel:${lead.phone}`;
  };

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-accent/20 hover:shadow-xl transition-all duration-300">
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Lead Info */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                lead.bookingType === 'direct' ? 'bg-green-100' : 'bg-primary/10'
              }`}>
                <Icon 
                  name={lead.bookingType === 'direct' ? 'CreditCardIcon' : 'ChatBubbleLeftRightIcon'} 
                  size={20} 
                  className={lead.bookingType === 'direct' ? 'text-green-600' : 'text-primary'} 
                />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg text-foreground">
                  {lead.name}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {lead.bookingType === 'direct' ? 'Direct Booking' : 'Lead Inquiry'}
                  {lead.bookingType === 'direct' && lead.paymentStatus && (
                    <span className={`ml-2 px-2 py-0.5 rounded-full text-xs font-bold ${
                      lead.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                      lead.paymentStatus === 'pending'? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {lead.paymentStatus.toUpperCase()}
                    </span>
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Contact Details */}
          <div className="grid md:grid-cols-2 gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Icon name="EnvelopeIcon" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">{lead.email}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="PhoneIcon" size={16} className="text-muted-foreground" />
              <span className="text-sm text-foreground">{lead.phone}</span>
            </div>
            <div className="flex items-center gap-2">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
              </svg>
              <span className="text-sm text-foreground">{lead.whatsapp}</span>
            </div>
          </div>

          {/* Booking Details */}
          <div className="bg-gradient-to-r from-primary/5 to-secondary/5 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-3">
              <Icon 
                name={lead.bookingDetails.type === 'flight' ? 'PaperAirplaneIcon' : lead.bookingDetails.type === 'hotel' ? 'BuildingOffice2Icon' : 'GlobeAltIcon'} 
                size={20} 
                className="text-primary" 
              />
              <h4 className="font-heading font-black text-foreground capitalize">
                {lead.bookingDetails.type} Booking
              </h4>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm">
              {lead.bookingDetails.from && (
                <div>
                  <span className="text-muted-foreground">From: </span>
                  <span className="font-bold text-foreground">{lead.bookingDetails.from}</span>
                </div>
              )}
              {lead.bookingDetails.to && (
                <div>
                  <span className="text-muted-foreground">To: </span>
                  <span className="font-bold text-foreground">{lead.bookingDetails.to}</span>
                </div>
              )}
              {lead.bookingDetails.destination && (
                <div>
                  <span className="text-muted-foreground">Destination: </span>
                  <span className="font-bold text-foreground">{lead.bookingDetails.destination}</span>
                </div>
              )}
              {lead.bookingDetails.date && (
                <div>
                  <span className="text-muted-foreground">Date: </span>
                  <span className="font-bold text-foreground">{lead.bookingDetails.date}</span>
                </div>
              )}
              {lead.bookingDetails.passengers && (
                <div>
                  <span className="text-muted-foreground">Passengers: </span>
                  <span className="font-bold text-foreground">{lead.bookingDetails.passengers}</span>
                </div>
              )}
              {lead.bookingDetails.price && lead.bookingDetails.price > 0 && (
                <div>
                  <span className="text-muted-foreground">Est. Price: </span>
                  <span className="font-bold text-primary">£{lead.bookingDetails.price}</span>
                </div>
              )}
            </div>
          </div>

          {/* Message */}
          {lead.message && (
            <div className="bg-surface rounded-lg p-3">
              <p className="text-xs font-bold text-muted-foreground mb-1">Additional Requirements:</p>
              <p className="text-sm text-foreground">{lead.message}</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="lg:w-48 space-y-3">
          <button
            onClick={handleWhatsApp}
            className="w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
              <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
            </svg>
            <span>WhatsApp</span>
          </button>
          <button
            onClick={handleCall}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Icon name="PhoneIcon" size={18} />
            <span>Call</span>
          </button>
          <button
            onClick={handleEmail}
            className="w-full bg-gray-600 hover:bg-gray-700 text-white font-bold py-3 rounded-lg transition-all duration-300 flex items-center justify-center gap-2"
          >
            <Icon name="EnvelopeIcon" size={18} />
            <span>Email</span>
          </button>

          {/* Status Update */}
          <div className="pt-3 border-t border-border">
            <label className="block text-xs font-bold text-muted-foreground mb-2">
              Update Status
            </label>
            <select
              value={lead.status}
              onChange={(e) => onStatusChange(e.target.value as Lead['status'])}
              className="w-full px-3 py-2 rounded-lg border-2 border-border focus:border-primary focus:outline-none text-sm font-bold"
            >
              <option value="new">New</option>
              <option value="contacted">Contacted</option>
              <option value="quoted">Quoted</option>
              <option value="booked">Booked</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>

          <button
            onClick={onDelete}
            className="w-full bg-red-50 hover:bg-red-100 text-red-600 font-bold py-2 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 border border-red-200"
          >
            <Icon name="TrashIcon" size={16} />
            <span>Delete</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default LeadCard;